/**
 * Football result-string parser. Ports the three parse sites in
 *   packages/amgfootball_v2/lib/presentation/bloc/game_bloc.dart
 * into one helper.
 *
 * Result string format (single-player /bet and multiplayer StartPlay):
 *   "leftCard,rightCard,centerCard,@winningBetItem"
 *   e.g. "1,7,8,@337"   (≥4 comma parts)
 *
 *   parts[0] = leftCard, parts[1] = rightCard, parts[2] = centerCard,
 *   parts[3] = winning bet item, with a leading '@' (stripped).
 *
 * Single-player strips parts[3].substring(1) UNCONDITIONALLY; multiplayer
 * strips '@' only when present. We strip any leading non-digit so both
 * forms ("@337" and "337") resolve to 337 safely.
 *
 * GameInitialInfo (next-round cards) is a separate "leftCard,rightCard" CSV.
 */
import type { BetType } from '../types/game';
import { betTypeFromId } from '../types/game';

export interface ParsedResult {
  leftCard: number;
  rightCard: number;
  centerCard: number;
  winningBetId: number;
  winningBet: BetType;
}

function parseIntSafe(s: string): number {
  const m = /-?\d+/.exec(s);
  return m ? parseInt(m[0], 10) : 0;
}

export function parseResultString(raw: string): ParsedResult {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length < 4) {
    throw new Error(`Invalid football result format: ${raw}`);
  }

  const leftCard = parseIntSafe(parts[0]);
  const rightCard = parseIntSafe(parts[1]);
  const centerCard = parseIntSafe(parts[2]);

  // Strip a leading '@' (or any non-digit prefix) from the winning item.
  const winRaw = parts[3].replace(/^[^0-9-]+/, '');
  const winningBetId = parseInt(winRaw, 10);
  const winningBet = betTypeFromId(winningBetId);
  if (!winningBet) {
    throw new Error(`Unknown winning betId ${winningBetId} in result: ${raw}`);
  }

  return { leftCard, rightCard, centerCard, winningBetId, winningBet };
}

/** "card1,card2" → [leftCard, rightCard]; missing/blank → [0, 0]. */
export function parseGameInitialInfo(raw: string | null | undefined): [number, number] {
  if (!raw) return [0, 0];
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return [parts[0] ? parseIntSafe(parts[0]) : 0, parts[1] ? parseIntSafe(parts[1]) : 0];
}
